import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, fromEvent } from 'rxjs';
import { filter, first, timeout } from 'rxjs/operators';

import {
	BrowserExtensionRequestMessageDataMap,
	BrowserExtensionRequestMessageKey,
	BrowserExtensionRequestToResponseKeyMap,
	BrowserExtensionRequestToResponseKeyMapObj,
	BrowserExtensionResponseMessageDataMap,
} from './browser-extension.types';

@Injectable({ providedIn: 'root' })
export class BrowserExtensionService {
	private readonly destroyRef = inject(DestroyRef);
	public get className(): string {
		return 'BrowserExtensionService';
	}

	public async sendMessage<
		REQ_KEY extends BrowserExtensionRequestMessageKey,
		REQ_DATA extends REQ_KEY extends keyof BrowserExtensionRequestMessageDataMap
			? BrowserExtensionRequestMessageDataMap[REQ_KEY]
			: Record<string, never>,
		RES_KEY extends BrowserExtensionRequestToResponseKeyMap[REQ_KEY],
		RES_DATA extends RES_KEY extends keyof BrowserExtensionResponseMessageDataMap
			? BrowserExtensionResponseMessageDataMap[RES_KEY]
			: Record<string, never>,
	>(key: REQ_KEY, data: REQ_DATA, timeoutMs = 2_000): Promise<RES_DATA> {
		return await new Promise<RES_DATA>(async (res, rej) => {
			try {
				const resKey = BrowserExtensionRequestToResponseKeyMapObj[key];

				const obs$ = fromEvent<MessageEvent<{ key: RES_KEY; data: RES_DATA }>>(
					window,
					'message'
				).pipe(
					filter((event) => {
						try {
							const { key } = event.data as {
								key: RES_KEY;
							};

							return key === resKey;
						} catch (error) {
							return false;
						}
					}),
					timeout(timeoutMs),
					first(),
					takeUntilDestroyed(this.destroyRef)
				);

				firstValueFrom(obs$)
					.then((event) => {
						res(event.data.data);
					})
					.catch((error) => {
						rej(error);
					});

				window.postMessage({ key, data }, window.location.href);
			} catch (err) {
				rej(err);
			}
		});
	}
}
